import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Button from '../../components/Button';
import supabase from '../../lib/supabase';

const passwordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export default function UpdatePassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  // Check if we have a valid reset token on mount
  useEffect(() => {
    const checkResetToken = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // If no session and no reset token in URL, redirect to auth
      if (!session && !window.location.hash && !searchParams.get('source')) {
        navigate('/auth');
        return;
      }
    };

    checkResetToken();
  }, [navigate, searchParams]);

  const onSubmit = async (data: PasswordFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password
      });

      if (updateError) throw updateError;

      // Sign out after password update
      await supabase.auth.signOut();
      
      // Redirect to auth with success message
      navigate('/auth?message=Password updated successfully. Please sign in with your new password.');
    } catch (error: any) {
      console.error('Password update error:', error);
      setError(error.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8 flex flex-col">
      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-dillan text-primary mb-2">
            Update Your Password
          </h1>
          <p className="text-neutral text-m font-dillan">
            Please enter your new password below.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-full bg-secondary-dark bg-opacity-10 text-primary text-sm flex items-start !text-primary">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-primary" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                {...register('password')}
                className="w-full h-12 px-4 pr-12 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 
      focus:outline-none font-dillan text-m"
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-primary">{errors.password.message}</p>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                {...register('confirmPassword')}
                className="w-full h-12 px-4 pr-12 rounded-full border-4 border-neutral bg-surface-light text-neutral placeholder:text-neutral focus:border-neutral focus:ring-0 
      focus:outline-none font-dillan text-m"
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-primary">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="text-m text-surface-light"
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/auth')}
            className="text-m text-surface-light"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}